import React from 'react';

export const fmt = (n) =>
  new Intl.NumberFormat('ru-RU', { style: 'decimal', maximumFractionDigits: 0 }).format(n ?? 0);

export const fmtMoney = (n) =>
  new Intl.NumberFormat('ru-RU', { style: 'decimal', maximumFractionDigits: 0 }).format(n ?? 0) + ' ₸';

export const fmtPct = (n) =>
  `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(n ?? 0)}%`;

export const MONTH_NAMES = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];

export function monthLabel(ym) {
  if (!ym) return '';
  const [, m] = ym.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${ym.split('-')[0]}`;
}

export function SummaryCard({ icon: Icon, label, value, sub, color, footer }) {
  return (
    <div className="rounded-xl border-0 bg-white shadow-sm p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      {footer}
    </div>
  );
}

export function HorizontalBar({ value, max, color = 'bg-blue-500' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 bg-gray-100 rounded-full h-2">
        <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
    </div>
  );
}

export function MonthlyChart({ data, valueKey, label, color = '#3b82f6' }) {
  if (!data || data.length === 0) {
    return <p className="text-gray-400 text-sm py-8 text-center">Нет данных</p>;
  }
  const max = Math.max(...data.map((d) => d[valueKey] || 0), 1);
  return (
    <div className="flex items-end gap-2 h-40 mt-4">
      {data.map((d) => {
        const h = Math.round(((d[valueKey] || 0) / max) * 140);
        return (
          <div key={d.month} className="flex-1 flex flex-col items-center gap-1">
            <div
              className="w-full rounded-t-sm transition-all"
              style={{ height: `${h}px`, backgroundColor: color, minHeight: h > 0 ? '4px' : '0' }}
              title={`${monthLabel(d.month)}: ${label === 'amount' ? fmtMoney(d[valueKey]) : fmt(d[valueKey])}`}
            />
            <span className="text-xs text-gray-400 leading-tight text-center">
              {monthLabel(d.month).split(' ')[0]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function TopProductsList({ items, valueKey, valueLabel, barColor = 'bg-blue-500' }) {
  if (!items || items.length === 0) {
    return <p className="text-gray-400 text-sm">Нет данных</p>;
  }
  const maxVal = items[0]?.[valueKey] || 1;
  return (
    <div className="space-y-3">
      {items.map((p, i) => (
        <div key={p.product_id || i}>
          <div className="flex items-start justify-between mb-1">
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <span className={`text-xs font-bold w-5 shrink-0 mt-0.5 ${i < 3 ? 'text-yellow-500' : 'text-gray-400'}`}>
                {i + 1}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate leading-tight">{p.product_name}</p>
                <p className="text-xs text-gray-400">
                  {p.product_code} · {p.orders_count} заказ{p.orders_count === 1 ? '' : p.orders_count < 5 ? 'а' : 'ов'}
                </p>
              </div>
            </div>
            <span className="text-sm font-semibold text-blue-700 shrink-0 ml-2">{valueLabel(p)}</span>
          </div>
          <div className="pl-7">
            <HorizontalBar value={p[valueKey]} max={maxVal} color={barColor} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function StatsLoading() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
    </div>
  );
}

export function StatsError({ message, onRetry }) {
  return (
    <div className="text-center py-24">
      <p className="text-red-500 mb-4">{message}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} className="text-sm text-blue-600 hover:underline">
          Повторить
        </button>
      )}
    </div>
  );
}
